import { createFileRoute, redirect } from '@tanstack/react-router'
import Loader from '@/components/common/Loader'
import { getPermitsById } from '@/features/city-clerk/dashboard-permit-tracking/api/permit-api'
import { checklistTemplatesQueryOptions } from '@/features/city-clerk/permit-checklist/hooks/useChecklistTemplates'
import { selectedChecklistQueryOptions } from '@/features/city-clerk/permit-checklist/hooks/useSelectedChecklist'
import { lazy, Suspense } from 'react'

const NewPermitChecklistSetup = lazy(async () => {
  const module = await import(
    '@/features/city-clerk/permit-checklist/pages/NewPermitChecklistSetup/NewPermitChecklistSetup'
  )
  return { default: module.default }
})

export const Route = createFileRoute(
  '/(app)/city-clerk/$id/projects/$projectId/permit-checklist-setup/$permitId/',
)({
  beforeLoad: ({ context }) => {
    if (context.auth.loading) return
    if (!context.auth.userInfo?.uiCapabilities?.firstSubmittalView) {
      throw redirect({ to: '/access-denied' })
    }
  },
  loader: async ({ context: { queryClient }, params: { id, permitId } }) => {
    const permit = await queryClient.fetchQuery({
      queryKey: ['permit', permitId] as const,
      queryFn: () => getPermitsById(permitId),
    })

    await Promise.all([
      queryClient.fetchQuery(checklistTemplatesQueryOptions(id)),
      permit?.currentTemplateVersionId
        ? queryClient.fetchQuery(
            selectedChecklistQueryOptions(permit.currentTemplateVersionId),
          )
        : Promise.resolve(),
    ])
  },
  component: PermitChecklistSetupPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      from:
        (search.from as
          | 'administrative-check'
          | 'overall-permits'
          | 'permit-tracking') || undefined,
    }
  },
})

function PermitChecklistSetupPage() {
  const { id, projectId, permitId } = Route.useParams()

  return (
    <Suspense
      fallback={
        <div className="h-page bg-(--color-surface) flex items-center justify-center z-9999">
          <Loader size="lg" />
        </div>
      }
    >
      <NewPermitChecklistSetup
        projectId={projectId}
        permitId={permitId}
        clerkId={id}
      />
    </Suspense>
  )
}
